import React from 'react';
import { Box, Button, Typography, Paper } from '@mui/material';
import { ErrorOutline } from '@mui/icons-material';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** Shown above the reload button - lets a caller give more specific
   * guidance (e.g. "Try going back to the dashboard") than the default. */
  fallbackDescription?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * There was no React error boundary anywhere in the app (Admin Medium
 * #13, AUDIT_FIX_LOG.md) - an uncaught render error on any single page
 * crashed the whole app to React's default blank white screen, with no
 * way back except a manual browser refresh. This catches render errors
 * in whatever subtree it wraps and shows a recoverable fallback instead.
 *
 * Error boundaries can only be class components - there's no hook
 * equivalent (getDerivedStateFromError/componentDidCatch have no `useX`
 * form as of React 18/19).
 */
class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Uncaught render error:', error, errorInfo);
  }

  private handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          minHeight="50vh"
          p={3}
        >
          <Paper
            elevation={0}
            sx={{
              p: 4,
              textAlign: 'center',
              maxWidth: 480,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <ErrorOutline color="error" sx={{ fontSize: 48, mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Something went wrong
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {this.props.fallbackDescription ||
                'This page ran into an unexpected error. Reloading usually fixes it.'}
            </Typography>
            <Button variant="contained" onClick={this.handleReload}>
              Reload page
            </Button>
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
